import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getAuth } from 'firebase/auth';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Share2, Loader2, Trash2, UserPlus } from 'lucide-react';
import { apiFetch } from '@/lib/api';

interface Member {
  uid: string;
  role: string;
  email?: string;
  added_at?: string;
  added_by?: string;
}

const ROLES = ['viewer', 'editor', 'owner'] as const;

/**
 * Project sharing. The OWNER can invite collaborators by email, change their
 * role (viewer/editor/owner), and remove them. Members see the roster read-only.
 * Email→uid resolution happens at the proxy (POST /api/users/lookup); the engine
 * only ever stores a proxy-resolved uid.
 */
export default function SharePanel({ projectId, ownerUid }: { projectId: string; ownerUid: string }) {
  const { t } = useTranslation('production');
  const [members, setMembers] = useState<Member[]>([]);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<string>('editor');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const currentUid = getAuth().currentUser?.uid || '';
  const isOwner = !!currentUid && currentUid === ownerUid;

  const flash = (m: string) => {
    setMsg(m);
    setTimeout(() => setMsg(null), 4000);
  };

  const load = useCallback(() => {
    apiFetch(`/api/projects/${projectId}/members`)
      .then((r) => (r.ok ? r.json() : { members: [] }))
      .then((d) => setMembers(d.members || []))
      .catch(() => {});
  }, [projectId]);

  useEffect(load, [load]);

  const handleAdd = async () => {
    const e = email.trim();
    if (!e) return;
    setBusy(true);
    try {
      // 1) resolve the invitee's email to a uid at the proxy.
      const lookup = await apiFetch('/api/users/lookup', {
        method: 'POST',
        body: JSON.stringify({ email: e }),
      });
      if (!lookup.ok) {
        const d = await lookup.json().catch(() => ({}));
        flash(d.error || (lookup.status === 404 ? t('share.noUser') : t('share.lookupFailed')));
        return;
      }
      const resolved = await lookup.json();
      // 2) add the resolved uid as a member.
      const add = await apiFetch(`/api/projects/${projectId}/members`, {
        method: 'POST',
        body: JSON.stringify({ uid: resolved.uid, email: resolved.email || e, role }),
      });
      if (add.ok) {
        flash(`✓ Added ${resolved.email || e} as ${role}.`);
        setEmail('');
        load();
      } else {
        const d = await add.json().catch(() => ({}));
        flash(`Could not add: ${d.detail || add.status}`);
      }
    } catch {
      flash(t('share.networkError'));
    } finally {
      setBusy(false);
    }
  };

  const handleRole = async (uid: string, newRole: string) => {
    try {
      const resp = await apiFetch(`/api/projects/${projectId}/members/${uid}`, {
        method: 'PATCH',
        body: JSON.stringify({ role: newRole }),
      });
      if (resp.ok) load();
      else {
        const d = await resp.json().catch(() => ({}));
        flash(`Could not change role: ${d.detail || resp.status}`);
      }
    } catch {
      flash(t('share.networkError'));
    }
  };

  const handleRemove = async (uid: string) => {
    try {
      const resp = await apiFetch(`/api/projects/${projectId}/members/${uid}`, { method: 'DELETE' });
      if (resp.ok) load();
      else {
        const d = await resp.json().catch(() => ({}));
        flash(`Could not remove: ${d.detail || resp.status}`);
      }
    } catch {
      flash(t('share.networkError'));
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-sm font-medium">
          <Share2 className="h-4 w-4" />
          {t('share.heading')}
        </CardTitle>
        <CardDescription>{isOwner ? t('share.subtitleOwner') : t('share.subtitle')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {msg && <div className="text-[11px] text-muted-foreground">{msg}</div>}

        {isOwner && (
          <div className="flex items-center gap-1.5">
            <input
              className="flex-1 min-w-0 border rounded px-2 py-1 text-xs bg-background"
              placeholder={t('share.placeholder')}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleAdd();
              }}
            />
            <select
              className="text-xs border rounded px-1 py-1 bg-background"
              value={role}
              onChange={(e) => setRole(e.target.value)}
            >
              <option value="viewer">{t('share.role.viewer')}</option>
              <option value="editor">{t('share.role.editor')}</option>
            </select>
            <Button
              variant="outline"
              size="sm"
              className="h-7 px-2 text-[10px] flex-shrink-0"
              disabled={busy || !email.trim()}
              onClick={handleAdd}
            >
              {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <UserPlus className="mr-1 h-3 w-3" />}
              {t('share.add')}
            </Button>
          </div>
        )}

        {members.length === 0 ? (
          <p className="text-xs text-muted-foreground">{t('share.empty')}</p>
        ) : (
          <div className="space-y-1.5">
            {members.map((m) => {
              const isProjectOwner = m.uid === ownerUid;
              return (
                <div key={m.uid} className="flex items-center gap-2 border border-border rounded px-2 py-1.5">
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium truncate">{m.email || m.uid}</div>
                    {m.email && <div className="text-[10px] text-muted-foreground truncate">{m.uid}</div>}
                  </div>
                  {isOwner && !isProjectOwner ? (
                    <>
                      <select
                        className="text-[10px] border rounded px-1 py-0.5 bg-background"
                        value={m.role}
                        onChange={(e) => handleRole(m.uid, e.target.value)}
                      >
                        {ROLES.map((r) => (
                          <option key={r} value={r}>
                            {t(`share.role.${r}`, { defaultValue: r })}
                          </option>
                        ))}
                      </select>
                      <button
                        className="text-muted-foreground hover:text-red-500 p-1"
                        title={t('share.removeTitle')}
                        onClick={() => handleRemove(m.uid)}
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </>
                  ) : (
                    <Badge variant={isProjectOwner ? 'success' : 'secondary'} className="px-1 text-[9px]">
                      {t(`share.role.${m.role}`, { defaultValue: m.role })}
                    </Badge>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
